import AntFundProjectionScreenOutlined from "@ant-design/icons/FundProjectionScreenOutlined";
import styled, { useTheme } from "styled-components";
import AccountCards from "features/dashboard/Accounts/AccountCards";
import PhoneHero from "features/dashboard/PhoneDashboard/PhoneHero";
import PhoneTiles from "features/dashboard/PhoneDashboard/PhoneTiles";
import PhoneAccountsList from "features/dashboard/PhoneDashboard/PhoneAccountsList";
import OverviewPanel from "features/dashboard/instrument/OverviewPanel.tsx";
import AccountsPanel from "features/dashboard/instrument/AccountsPanel.tsx";
import useDashboardAccounts from "features/dashboard/common/useDashboardAccounts.ts";
import {
  getNewLookTokens,
  NEW_LOOK_GAP,
  NEW_LOOK_CONTENT_MAX,
} from "common/NewLook/tokens.ts";
import TradeCards from "features/dashboard/OpenTrades/TradeCards";
import React, { useContext, useEffect, useState } from "react";
import {
  HIDE_INTERCOM_LAUNCHER_BREAKPOINT,
  SMALL_SCREEN,
  INTERCOM_BOTTOM,
  INTERCOM_ICON_SIZE,
  ACTION_BUTTON_MARGIN,
} from "common/constants";
import StatisticsColumn from "features/dashboard/Statistics/StatisticsColumn";
import MarketTable from "features/dashboard/Market/MarketTable";
import { StylableButton } from "common/Buttons/Button";
import { useSelector } from "react-redux";
import { selectConsolidatedAccountsPortfolioData } from "@shared/slices/portfolio.slice";
import useMedia from "utils/hooks/useMedia";
import Drawer from "common/Drawers/Drawer";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { ConsolidatedAccountData } from "@shared/types";
import SocketPnlProvider from "@shared/features/dashboard/SocketPnlProvider";
import { sharedLib } from "@shared/services/shared.service";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc";
import Statistics from "features/dashboard/MobileDashboard/Statistics";
import TopCoinsPrice from "features/dashboard/MobileDashboard/TopCoinsPrice";
import Balance from "features/dashboard/MobileDashboard/Balance";
import { TIME_RANGE_1_DAY } from "@shared/common/constants";
import { useSharedDashboard } from "@shared/features/dashboard/hooks";
import MobileTradesCards from "features/dashboard/MobileDashboard/MobileTradesCards";
import BotsSummaryCards from "features/dashboard/MobileDashboard/Bots/BotsSummaryCards";
import { MOBILE_GET_OPEN_TRADES_ARGS } from "@shared/features/trades/common/constants";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import { dashboardFilters } from "@shared/slices/filters.slice";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import ScrollView from "common/Scroll/ScrollView";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import SubUserNoClientsModal from "features/dashboard/SubUserNoClientsModal.tsx";
import { DASHBOARD_PAGE_ID } from "testIds.ts";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook/layout.ts";

const Dashboard = () => {
  const { setShowSearch } = useContext(PageWrapperContext);
  const { analytics } = useContext(AnalyticsContext);
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const [statsColumnExpanded, setStatsColumnExpanded] = useState(
    !isSmallScreen
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState(TIME_RANGE_1_DAY);
  const intercomLauncherVisible = useMedia(
    [`(max-width: ${HIDE_INTERCOM_LAUNCHER_BREAKPOINT})`],
    [false],
    true
  );
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();
  const { isReadOnly } = useSubUserPermissions();
  useDisablePageScroll(isBottomNavigator);
  const withPriceGraphs = isBottomNavigator || isNewLook;
  const {
    getDashboardDataStatus,
    isFetching,
    isLoading,
    refetchDashboardData,
    balanceFetching,
  } = useSharedDashboard({
    selectedTimeRange,
    withPriceGraphs,
  });

  usePageWrapper({
    filters: {
      actions: dashboardFilters.actions,
      selectors: dashboardFilters.selectors,
    },
    showSearch: false,
  });

  useEffect(() => {
    setShowSearch(false);
  }, []);

  useEffect(() => {
    analytics?.dashboardViewAppWeb({});
  }, [analytics]);

  const getTradesArgs = withPriceGraphs
    ? MOBILE_GET_OPEN_TRADES_ARGS
    : undefined;

  sharedLib.sharedApi.portfolio.useGetPortfolioDataQuery(undefined);
  const accountsData: ConsolidatedAccountData[] = useSelector(
    selectConsolidatedAccountsPortfolioData
  );
  // The new look's accounts follow the dashboard filter, each counted once.
  const dashboardAccounts = useDashboardAccounts();

  const showMobileStatsDrawer = () => {
    setStatsColumnExpanded(true);
  };
  const closeMobileStatsDrawer = () => {
    setStatsColumnExpanded(false);
  };

  if (isNewLook && !isBottomNavigator) {
    // Desktop dashboard exploration ("Instrument"): a tinted canvas with one
    // hero panel (portfolio value and its history beside what is in open
    // trades), then open trades and accounts, then markets.
    return (
      <SocketPnlProvider getTradesArgs={getTradesArgs}>
        <Canvas data-testid={DASHBOARD_PAGE_ID}>
          <OverviewPanel
            selectedTimeRange={selectedTimeRange}
            setSelectedTimeRange={setSelectedTimeRange}
            balanceLoading={balanceFetching}
          />
          <Columns>
            <TradeCards getTradesArgs={getTradesArgs} />
            <AccountsPanel accountsData={dashboardAccounts} />
          </Columns>
          <MarketTable />
        </Canvas>
        <SubUserNoClientsModal />
      </SocketPnlProvider>
    );
  }

  if (isNewLook) {
    // Phone: a blue header with the portfolio value and its history, figure
    // tiles overlapping its edge, then accounts, open trades and markets.
    return (
      <SocketPnlProvider getTradesArgs={getTradesArgs}>
        <MobileWrapper>
          <ScrollView
            onRefresh={refetchDashboardData}
            refreshing={isFetching}
            contentWrapperStyle={{ padding: 0 }}
            hideScrollBar={true}
            ignoreExternalRefreshing={true}
          >
            <PhoneWrapper data-testid={DASHBOARD_PAGE_ID}>
              <PhoneHero
                selectedTimeRange={selectedTimeRange}
                setSelectedTimeRange={setSelectedTimeRange}
                balanceLoading={balanceFetching}
              />
              <PhoneTiles />
              <TradeCards getTradesArgs={getTradesArgs} />
              <PhoneAccountsList accountsData={dashboardAccounts} maxRows={3} />
              <MarketTable />
            </PhoneWrapper>
          </ScrollView>
        </MobileWrapper>
        <SubUserNoClientsModal />
      </SocketPnlProvider>
    );
  }

  if (isBottomNavigator) {
    return (
      <SocketPnlProvider getTradesArgs={getTradesArgs}>
        <MobileWrapper data-testid={DASHBOARD_PAGE_ID}>
          <FiltersChips
            filtersActions={dashboardFilters.actions}
            filtersSelectors={dashboardFilters.selectors}
            style={CHIPS_STYLE}
          />
          <ScrollView
            loading={isLoading}
            onRefresh={refetchDashboardData}
            refreshing={isFetching}
            contentWrapperStyle={{ padding: 0 }}
            hideScrollBar={true}
            ignoreExternalRefreshing={balanceFetching}
          >
            <InnerWrapper>
              <Balance
                selectedTimeRange={selectedTimeRange}
                setSelectedTimeRange={setSelectedTimeRange}
                isFetching={balanceFetching}
                isSuccess={getDashboardDataStatus.isSuccess}
              />
              <TopCoinsPrice />
              <Separator />
              <Statistics />
            </InnerWrapper>
            <MobileTradesCards />
            {!isReadOnly && <BotsSummaryCards />}
          </ScrollView>
        </MobileWrapper>
        <SubUserNoClientsModal />
      </SocketPnlProvider>
    );
  }

  return (
    <SocketPnlProvider getTradesArgs={getTradesArgs}>
      <AccountsTradesStatsWrapper data-testid={DASHBOARD_PAGE_ID}>
        <AccountsTradesWrapper $isMobile={isSmallScreen}>
          <SectionWrapper $isMobile={isSmallScreen}>
            <TradeCards getTradesArgs={getTradesArgs} />
          </SectionWrapper>
          <SectionWrapper $isMobile={isSmallScreen}>
            <AccountCards accountsData={accountsData} />
          </SectionWrapper>
        </AccountsTradesWrapper>
        {!isSmallScreen && (
          <StatisticsWrapper>
            <StatisticsColumn />
          </StatisticsWrapper>
        )}
      </AccountsTradesStatsWrapper>
      {!isSmallScreen && <HorizontalBorder />}
      <MarketTable />
      {isSmallScreen && (
        <>
          <DrawerButton
            onClick={showMobileStatsDrawer}
            icon={<FundProjectionScreenOutlined />}
            style={
              intercomLauncherVisible
                ? { right: 90, bottom: INTERCOM_BOTTOM }
                : {}
            }
          />
          <Drawer
            open={statsColumnExpanded}
            onClose={closeMobileStatsDrawer}
            destroyOnHidden={true}
            size="auto"
            styles={{ body: { padding: 16 } }}
            closable={false}
          >
            <StatisticsColumn />
          </Drawer>
        </>
      )}
      <SubUserNoClientsModal />
    </SocketPnlProvider>
  );
};

export default React.memo(Dashboard);

const CHIPS_STYLE = { width: "100%" };

const InnerWrapper = styled.div`
  padding: 16px 16px 24px;
`;
const MobileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
`;
const PhoneWrapper = styled.div`
  min-width: 0;
  min-height: 100%;
  padding-bottom: 24px;
`;
const Canvas = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_GAP}px;
  min-height: 100%;
  width: 100%;
  max-width: ${NEW_LOOK_CONTENT_MAX + 64}px;
  margin: 0 auto;
  padding: 24px ${NEW_LOOK_PAGE_GUTTER_VAR} 40px;
  background: ${(p) => getNewLookTokens(p.theme).canvas};
`;
const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(0, 1fr);
  gap: ${NEW_LOOK_GAP}px;
  align-items: stretch;
  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const AccountsTradesStatsWrapper = styled.div`
  display: flex;
  width: 100%;
`;
const AccountsTradesWrapper = styled.div<{ $isMobile: boolean }>`
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding: 16px ${(p) => (p.$isMobile ? 10 : 16)}px 0;
  flex: 1;
  justify-content: space-between;
  margin-top: ${(p) => (p.$isMobile ? 0 : 16)}px;
`;
const SectionWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;
const HorizontalBorder = styled.div`
  border-bottom: 3px solid ${(p) => p.theme.colors.dashboardStatisticsBgColor};
  width: 100%;
`;
const FundProjectionScreenOutlined = styled(AntFundProjectionScreenOutlined)`
  font-size: 24px !important;
`;
const StatisticsWrapper = styled.div`
  padding: 16px 20px;
  background: ${(p) => p.theme.colors.dashboardStatisticsBgColor};
  height: auto;
  display: flex;
`;
const DrawerButton = styled(StylableButton).attrs({
  shape: "circle",
  type: "primary",
  size: "middle",
})`
  position: fixed;
  bottom: ${ACTION_BUTTON_MARGIN}px;
  right: 16px;
  z-index: 2;
  width: ${INTERCOM_ICON_SIZE}px !important;
  height: ${INTERCOM_ICON_SIZE}px !important;
`;
const Separator = styled.div`
  border-bottom: ${(p) => p.theme.borders.light};
  width: 100%;
  margin-bottom: 16px;
  padding-top: 12px;
`;
